<template>
  <VModal :showing="open" container-classes="max-w-md" @update:showing="onOpenChange">
    <template #header>
      <div class="flex items-center gap-2 text-lg font-semibold text-gray-900">
        <FontAwesomeIcon icon="newspaper" class="w-5 h-5 text-gray-600" />
        Press release
      </div>
      <p class="sr-only">Create or manage a public stakeholder link for this presentation.</p>
    </template>
    <template #default>
      <div v-if="!link" class="flex flex-col gap-4">
        <div class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-10 flex flex-col items-center text-center gap-3">
          <FontAwesomeIcon icon="newspaper" class="w-10 h-10 text-gray-400" />
          <p class="font-semibold text-gray-900">No link created yet</p>
          <p class="text-sm text-gray-600 max-w-sm">
            Creating a link makes this presentation immediately live and accessible to stakeholders.
          </p>
        </div>
        <VButton class="w-full justify-center" variant="copper" :disabled="creating" @click="handleCreate">
          <FontAwesomeIcon icon="link" class="w-4 h-4 mr-2" />
          Create link
        </VButton>
      </div>

      <div v-else class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span class="inline-flex items-center gap-2">
            <span class="w-2 h-2 shrink-0 rounded-full bg-emerald-500" aria-hidden="true" />
            <span class="text-sm font-medium text-emerald-700">Live</span>
          </span>
          <span class="text-sm text-gray-500">Since {{ formatDateMed(link.created_at) }}</span>
        </div>

        <div class="space-y-2">
          <label for="press-release-url" class="text-sm font-medium text-gray-900">Stakeholder link</label>
          <div class="flex gap-2">
            <VInput id="press-release-url" class="font-mono text-xs flex-1 min-w-0" readonly :model-value="stakeholderUrl" />
            <VButton type="button" variant="copper-outline" size="md" class="shrink-0" @click="handleCopy">
              <FontAwesomeIcon icon="link" class="w-4 h-4 mr-2" />
              {{ wasClicked ? 'Copied' : 'Copy' }}
            </VButton>
          </div>
          <p class="text-xs text-gray-500">Anyone with this link can view the presentation. Share it directly with stakeholders.</p>
        </div>

        <hr class="border-gray-200" />

        <VButton
          variant="flat"
          class="!text-sm text-red-700 hover_bg-red-50 justify-start border-0 shadow-none"
          @click="destroyConfirmShowing = true"
        >
          <FontAwesomeIcon icon="trash" class="w-4 h-4 mr-2" />
          Destroy link
        </VButton>
      </div>
    </template>
  </VModal>

  <Alert v-model:showing="destroyConfirmShowing" variant="danger" confirmation-label="Yes, destroy link" @confirm="handleDestroyConfirm">
    <template #title>Destroy this link?</template>
    The current link will stop working immediately for all stakeholders. You can create a new one afterwards, but it will be a different
    URL.
  </Alert>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { FetchError } from 'ofetch'
import type { DeepReadonly } from 'vue'
import type { EditDeck } from '~/composables/useEditDeck'
import { formatDateMed } from '@/lib/datetime-format-utils'
import Alert from '~/components/modals/Alert.vue'
import useCopyToClipboard from '~/composables/useCopyToClipboard'

const props = defineProps<{
  deck: DeepReadonly<EditDeck>
}>()

const open = defineModel<boolean>('open', { default: false })

const emit = defineEmits<{
  'refresh-deck': []
}>()

const config = useRuntimeConfig()
const { createPressReleaseLink, deletePressReleaseLink } = useDeckPressRelease()
const { setNotification } = useGlobalNotification()
const { copyToClipboard, wasClicked } = useCopyToClipboard()

const creating = ref(false)
const destroyConfirmShowing = ref(false)

const link = computed(() => props.deck.press_release_link ?? null)

const stakeholderUrl = computed(() => {
  if (!link.value) return ''
  return `${config.public.WEB_ENDPOINT}decks/${props.deck.id}?auth=${link.value.uuid}`
})

function onOpenChange(next: boolean) {
  open.value = next
}

async function handleCreate() {
  creating.value = true
  try {
    await createPressReleaseLink(props.deck.id)
    emit('refresh-deck')
    setNotification({
      type: 'success',
      title: 'Press release link created',
      delay: 5000,
    })
  } catch (e) {
    const msg =
      e instanceof FetchError && e.data && typeof (e.data as { message?: string }).message === 'string'
        ? String((e.data as { message: string }).message)
        : 'Could not create press release link.'
    setNotification({
      type: 'danger',
      title: 'Create link failed',
      text: msg,
      delay: 5000,
    })
  } finally {
    creating.value = false
  }
}

function handleCopy() {
  if (!stakeholderUrl.value) return
  copyToClipboard({ copyText: stakeholderUrl.value })
}

async function handleDestroyConfirm() {
  destroyConfirmShowing.value = false
  try {
    await deletePressReleaseLink(props.deck.id)
    emit('refresh-deck')
    setNotification({
      type: 'success',
      title: 'Press release link removed',
      delay: 5000,
    })
    open.value = false
  } catch (e) {
    const status = e instanceof FetchError ? e.response?.status : undefined
    if (status === 404) {
      emit('refresh-deck')
      open.value = false
      return
    }
    const msg =
      e instanceof FetchError && e.data && typeof (e.data as { message?: string }).message === 'string'
        ? String((e.data as { message: string }).message)
        : 'Could not remove press release link.'
    setNotification({
      type: 'danger',
      title: 'Remove link failed',
      text: msg,
      delay: 5000,
    })
  }
}
</script>
